import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from './ui/Button';
import Input from './ui/Input';
import Alert from './ui/Alert';

// SignPasswordModal — la fiecare „Semnează" cere parola pentru aplicarea AdES.
// onSubmit(password) primește parola; părintele face apelul API. Dacă apelul aruncă,
// expunem mesajul de eroare în modal și păstrăm modalul deschis pentru retry.
//
// Props:
//   title           — titlu (ex: t('signing.confirmTitle'))
//   description     — text scurt despre acțiunea ce urmează a fi semnată
//   onSubmit(pwd)   — funcție async; throw pentru a indica eșec → modalul rămâne deschis
//   onClose()       — închide modalul fără submit
const SignPasswordModal = ({ title, description, onSubmit, onClose }) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!password) {
      setError(t('modals.signPassword.required'));
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await onSubmit(password);
      // închiderea o face părintele (în onSuccess), nu modalul
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || t('toasts.errorGeneric');
      setError(msg);
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content max-w-md">
        <div className="modal-header">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary-100 dark:bg-primary-900/50 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg
                className="w-5 h-5 text-primary-600 dark:text-primary-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 11c0-1.1-.9-2-2-2s-2 .9-2 2v2h4v-2zm6 9H6a2 2 0 01-2-2v-6a2 2 0 012-2h12a2 2 0 012 2v6a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {title || t('modals.signPassword.title')}
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t('modals.signPassword.subtitle')}
              </p>
            </div>
          </div>
        </div>

        <div className="modal-body">
          {description && (
            <div className="mb-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm text-slate-700 dark:text-slate-300">
              {description}
            </div>
          )}

          {error && (
            <Alert type="error" className="mb-3">
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} id="sign-pwd-form">
            <Input
              type="password"
              label={t('modals.signPassword.passwordLabel')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              autoFocus
              disabled={loading}
            />
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
              {t('modals.signPassword.hint')}
            </p>
          </form>
        </div>

        <div className="modal-footer flex gap-2">
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            form="sign-pwd-form"
            loading={loading}
            disabled={!password || loading}
            className="flex-1"
          >
            {loading ? t('modals.signPassword.signing') : t('modals.signPassword.signBtn')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SignPasswordModal;
